import React, { useState, useEffect } from 'react';
import { Package, Search, Filter, Grid, List, ChevronRight, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const ProductPortfolio = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [productsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/trm/products?limit=500`),
        fetch(`${API_URL}/api/trm/categories`)
      ]);

      if (!productsRes.ok || !categoriesRes.ok) {
        throw new Error('Failed to fetch product data');
      }

      const productsData = await productsRes.json();
      const categoriesData = await categoriesRes.json();

      setProducts(productsData.products || []);
      setCategories(categoriesData.categories || []);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      product.canonical_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.manufacturer_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = 
      selectedCategory === 'all' || 
      product.trm_category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Group products by category
  const productsByCategory = filteredProducts.reduce((acc, product) => {
    const category = product.trm_category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(product);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p style={{ marginTop: '16px', color: 'var(--cares-text-label)' }}>Loading product portfolio...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))', borderRadius: '8px', border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))' }}>
        <AlertCircle style={{ color: 'var(--cares-orange)', width: '24px', height: '24px', marginBottom: '8px' }} />
        <h3 style={{ color: 'var(--cares-orange)', marginBottom: '8px' }}>Error Loading Data</h3>
        <p style={{ color: 'var(--cares-orange)' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', color: 'var(--cares-text-1)', marginBottom: '8px' }}>
          Product Portfolio
        </h1>
        <p style={{ color: 'var(--cares-text-label)', fontSize: '16px' }}>
          Explore {products.length} products across {Object.keys(productsByCategory).length} categories
        </p>
      </div>

      {/* Controls */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ flex: '1 1 300px', position: 'relative' }}>
          <Search style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: 'var(--cares-text-note)'
          }} />
          <input
            type="text"
            placeholder="Search products or manufacturers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid var(--cares-line-strong)',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '1px solid var(--cares-line-strong)',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
            minWidth: '180px',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Categories</option>
          {Object.keys(productsByCategory).sort().map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>

        {/* View Toggle */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setViewMode('grid')}
            style={{
              padding: '8px 12px',
              border: `2px solid ${viewMode === 'grid' ? 'var(--cares-blue)' : 'var(--cares-line-card)'}`,
              borderRadius: '8px',
              background: viewMode === 'grid' ? 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))' : 'var(--cares-surface-card)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Grid style={{ width: '16px', height: '16px', color: viewMode === 'grid' ? 'var(--cares-blue)' : 'var(--cares-text-label)' }} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            style={{
              padding: '8px 12px',
              border: `2px solid ${viewMode === 'list' ? 'var(--cares-blue)' : 'var(--cares-line-card)'}`,
              borderRadius: '8px',
              background: viewMode === 'list' ? 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))' : 'var(--cares-surface-card)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <List style={{ width: '16px', height: '16px', color: viewMode === 'list' ? 'var(--cares-blue)' : 'var(--cares-text-label)' }} />
          </button>
        </div>
      </div>

      {/* Products by Category */}
      {Object.keys(productsByCategory).sort().map(category => (
        <div key={category} style={{ marginBottom: '32px' }}>
          {/* Category Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '2px solid var(--cares-line-card)'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--cares-text-1)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Package style={{ width: '20px', height: '20px', color: 'var(--cares-blue)' }} />
              {category}
            </h2>
            <span style={{ 
              padding: '4px 12px',
              background: 'var(--cares-surface-board)',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '500',
              color: 'var(--cares-text-3)'
            }}>
              {productsByCategory[category].length} products
            </span>
          </div>

          {/* Product Cards/List */}
          <div style={{
            display: viewMode === 'grid' ? 'grid' : 'flex',
            gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'none',
            flexDirection: viewMode === 'list' ? 'column' : 'none',
            gap: '16px'
          }}>
            {productsByCategory[category].map(product => (
              <div
                key={product.product_id}
                style={{
                  background: 'var(--cares-surface-card)',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: viewMode === 'list' ? 'row' : 'column',
                  alignItems: viewMode === 'list' ? 'center' : 'flex-start',
                  justifyContent: viewMode === 'list' ? 'space-between' : 'flex-start'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = 'var(--cares-blue)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = 'var(--cares-line-card)';
                }}
              >
                <div style={{ flex: 1 }}>
                  {/* Product Name */}
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--cares-text-1)',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Package style={{ width: '16px', height: '16px', color: 'var(--cares-blue)' }} />
                    {product.canonical_name}
                  </h3>

                  {/* Manufacturer */}
                  <p style={{
                    fontSize: '13px',
                    color: 'var(--cares-text-label)',
                    marginBottom: '12px'
                  }}>
                    by {product.manufacturer_name}
                  </p>

                  {/* Stats */}
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      padding: '4px 10px',
                      background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                      color: 'var(--cares-blue)',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {product.version_count} versions
                    </span>
                    {product.record_status === 'Active' && (
                      <span style={{
                        padding: '4px 10px',
                        background: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))',
                        color: 'var(--cares-green)',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Active
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow Icon (for list view) */}
                {viewMode === 'list' && (
                  <ChevronRight style={{ width: '20px', height: '20px', color: 'var(--cares-text-note)' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* No Results */}
      {filteredProducts.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'var(--cares-surface-card)',
          borderRadius: '12px',
          border: '1px dashed var(--cares-line-strong)'
        }}>
          <Package style={{ width: '48px', height: '48px', color: 'var(--cares-text-note)', margin: '0 auto 16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-3)', marginBottom: '8px' }}>
            No Products Found
          </h3>
          <p style={{ color: 'var(--cares-text-label)' }}>
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductPortfolio;
